// ReportDetailScreen — full render of one archived digest (docs/miniapp-m6-tz.md
// §3.8). Director-only. Fetches GET /reports/:id; content_md is Telegram HTML
// (<b>/<i> + #id deal refs — written by ai_reports_store as tg_html), so we
// render bold/italic and linkify deal ids (#1986 → /deals/1986), mirroring the
// M-3 AIReportDetailScreen renderer.
import { type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { fetchReportDetail } from '../../api/reports'
import { Badge, Button, Icon, ScreenSkeleton, t } from '../../design'

function fmtDate(iso: string | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '')
  return m ? `${m[3]}.${m[2]}.${m[1]}` : (iso || '')
}

function renderLine(line: string, onDeal: (id: number) => void, keyBase: string): ReactNode {
  const tokens = line.split(/(<\/?b>|<\/?i>|#\d+)/gi).filter((x) => x !== '')
  let bold = false
  let italic = false
  const out: ReactNode[] = []

  tokens.forEach((tok, i) => {
    const low = tok.toLowerCase()
    if (low === '<b>') { bold = true; return }
    if (low === '</b>') { bold = false; return }
    if (low === '<i>') { italic = true; return }
    if (low === '</i>') { italic = false; return }

    const dealMatch = /^#(\d+)$/.exec(tok)
    if (dealMatch) {
      const id = Number(dealMatch[1])
      out.push(
        <button
          key={`${keyBase}-${i}`}
          onClick={() => onDeal(id)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--brand-500)', font: 'inherit', fontWeight: 600 }}
        >
          {tok}
        </button>,
      )
      return
    }

    const text = tok.replace(/<[^>]+>/g, '')
    if (!text) return
    out.push(
      <span key={`${keyBase}-${i}`} style={{ fontWeight: bold ? 700 : undefined, fontStyle: italic ? 'italic' : undefined }}>
        {text}
      </span>,
    )
  })

  return out
}

export function ReportDetailScreen() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['report', id],
    queryFn: () => fetchReportDetail(id),
  })

  if (isLoading) return <ScreenSkeleton kind="detail" />
  if (isError || !data) {
    return (
      <div className="scroll-y" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 14, padding: 24, textAlign: 'center' }}>
        <Icon.Activity size={30} style={{ color: 'var(--text-3)' }} />
        <div className="t-h2">{t('arch.notFound')}</div>
        <Button variant="primary" icon={<Icon.ArrowLeft size={16} sw={2} />} onClick={() => navigate('/reports')}>{t('air.back')}</Button>
      </div>
    )
  }

  const weekly = data.kind.includes('weekly')
  const lines = (data.content_md || '').split('\n')

  return (
    <div className="scroll-y" style={{ flex: 1, padding: '12px 16px 24px' }}>
      <div className="row middle gap-2" style={{ marginBottom: 4 }}>
        <Badge tone={weekly ? 'ai' : 'neutral'} sm>
          <Icon.Activity size={10} sw={2.5} />{weekly ? t('air.weekly') : t('air.daily')}
        </Badge>
        {data.generated_at && <span className="t-caption dim">{fmtDate(data.generated_at)}</span>}
      </div>
      <div className="t-h1" style={{ marginBottom: 10 }}>{data.title}</div>

      <div className="card card-16">
        <div className="col" style={{ gap: 2 }}>
          {lines.map((line, i) =>
            line.trim() === '' ? (
              <div key={i} style={{ height: 8 }} />
            ) : (
              <div key={i} className="t-body" style={{ lineHeight: 1.5 }}>
                {renderLine(line, (dealId) => navigate(`/deals/${dealId}`), String(i))}
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  )
}
